
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import PricingHero from './components/PricingHero';
import PricingCard from './components/PricingCard';
import PricingComparison from './components/PricingComparison';
import PricingFAQ from './components/PricingFAQ';
import SEO from '../../components/base/SEO';

export default function Pricing() {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');

  const pricingPlans = [
    {
      name: 'Starter',
      monthlyPrice: '$29',
      annualPrice: '$23',
      period: 'month',
      description: 'Perfect for individuals and solopreneurs',
      features: [
        { text: '50 generated posts per month', included: true },
        { text: 'Up to 3 social media accounts', included: true },
        { text: 'Basic analytics and reporting', included: true },
        { text: 'Content calendar', included: true },
        { text: 'Post scheduling', included: true },
        { text: 'AI content optimization', included: true },
        { text: 'Email support (48h response)', included: true },
        { text: 'Bulk scheduling', included: false },
        { text: 'Team collaboration', included: false },
        { text: 'Advanced analytics', included: false },
      ],
      ctaText: 'Start Free Trial',
    },
    {
      name: 'Professional',
      monthlyPrice: '$79',
      annualPrice: '$63',
      period: 'month',
      description: 'Ideal for small teams and growing businesses',
      features: [
        { text: '200 generated posts per month', included: true },
        { text: 'Up to 10 social media accounts', included: true },
        { text: 'Advanced analytics & reporting', included: true },
        { text: 'Up to 5 team members', included: true },
        { text: 'Approval workflows', included: true },
        { text: 'Kanban team boards', included: true },
        { text: 'AI trend analysis', included: true },
        { text: 'Bulk scheduling', included: true },
        { text: 'Priority support (24h response)', included: true },
        { text: 'Competitor insights', included: false },
      ],
      ctaText: 'Start Free Trial',
    },
    {
      name: 'Business',
      monthlyPrice: '$199',
      annualPrice: '$159',
      period: 'month',
      description: 'For established businesses scaling their presence',
      features: [
        { text: '1,000 generated posts per month', included: true },
        { text: 'Up to 25 social media accounts', included: true },
        { text: 'Custom reports & white-labeling', included: true },
        { text: 'Up to 15 team members', included: true },
        { text: 'Advanced workflows & automation', included: true },
        { text: 'Competitor insights & analysis', included: true },
        { text: 'API access', included: true },
        { text: 'Phone & email support (12h response)', included: true },
        { text: 'Custom integrations', included: true },
        { text: 'Dedicated account manager', included: false },
      ],
      ctaText: 'Start Free Trial',
      isPopular: true,
      badge: 'Most Popular',
    },
    {
      name: 'Enterprise',
      monthlyPrice: '$499',
      annualPrice: '$399',
      period: 'month',
      description: 'Advanced solutions for large organizations',
      features: [
        { text: '5,000 generated posts per month', included: true },
        { text: 'Up to 50 social media accounts', included: true },
        { text: 'Up to 50 team members', included: true },
        { text: 'White-label solution', included: true },
        { text: 'Dedicated account manager', included: true },
        { text: 'Advanced analytics & BI tools', included: true },
        { text: 'API access & custom integrations', included: true },
        { text: 'Priority support (4h response)', included: true },
        { text: 'Custom onboarding & training', included: true },
        { text: 'SLA guarantee (99.9% uptime)', included: true },
      ],
      ctaText: 'Start Free Trial',
    },
    {
      name: 'Custom',
      monthlyPrice: 'Custom',
      annualPrice: 'Custom',
      period: 'pricing',
      description: 'Fully tailored for enterprise-scale needs',
      features: [
        { text: 'Unlimited generated posts', included: true },
        { text: 'Unlimited social media accounts', included: true },
        { text: 'Unlimited team members', included: true },
        { text: 'Custom AI model training', included: true },
        { text: 'Multi-brand management', included: true },
        { text: 'Dedicated success team', included: true },
        { text: 'Custom feature development', included: true },
        { text: 'SLA guarantee (99.99% uptime)', included: true },
        { text: '24/7 priority support (1h response)', included: true },
        { text: 'On-premise deployment option', included: true },
      ],
      ctaText: 'Contact Sales',
    },
  ];

  return (
    <div className="min-h-screen bg-white font-['Montserrat']">
      <SEO
        title="Pricing - Affordable Plans for Every Team"
        description="Choose the perfect MandaPost plan for your team. Starting at just $29/month with 3 months free for early adopters. No credit card required to start your free trial."
        keywords="social media pricing, affordable social media management, pricing plans, subscription pricing, free trial"
        canonicalUrl="https://mandapost.com/pricing"
      />
      <Header />

      <PricingHero
        billingPeriod={billingPeriod}
        onBillingChange={setBillingPeriod}
      />

      {/* Pricing Cards */}
      <section className="pb-20 -mt-8">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {pricingPlans.map((plan, index) => (
              <PricingCard
                key={index}
                name={plan.name}
                price={
                  plan.monthlyPrice === 'Custom'
                    ? 'Custom'
                    : billingPeriod === 'monthly'
                    ? plan.monthlyPrice
                    : plan.annualPrice
                }
                period={plan.period}
                description={plan.description}
                features={plan.features}
                isPopular={plan.isPopular}
                ctaText={plan.ctaText}
                badge={plan.badge}
              />
            ))}
          </div>

          {/* Trust Indicators */}
          <div className="mt-16 grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-[#22577A] mb-2">14 Days</div>
              <p className="text-gray-600">Free Trial</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-[#22577A] mb-2">No Card</div>
              <p className="text-gray-600">Required</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-[#22577A] mb-2">24/7</div>
              <p className="text-gray-600">Support Available</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-[#22577A] mb-2">Cancel</div>
              <p className="text-gray-600">Anytime</p>
            </div>
          </div>
        </div>
      </section>

      {/* Video Tutorial Section */}
      <section className="py-20 bg-gradient-to-br from-[#e4f4ff]/20 to-[#b3d9ff]/20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 bg-[#e4f4ff]/40 rounded-full text-[#22577A] font-medium mb-6">
              <i className="ri-video-line mr-2"></i>
              Video Tutorial
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#22577A] mb-4">
              See How MandaPost Works
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Watch this quick tutorial to learn how to get started and make the most of your MandaPost subscription.
            </p>
          </div>

          {/* Video Placeholder */}
          <div className="relative bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-3xl overflow-hidden shadow-2xl">
            <div className="aspect-video flex items-center justify-center">
              {/* Placeholder Content */}
              <div className="text-center text-white p-8">
                <div className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-white/30">
                  <i className="ri-play-circle-line text-6xl"></i>
                </div>
                <h3 className="text-2xl font-bold mb-3">Video Tutorial Coming Soon</h3>
                <p className="text-white/80 text-lg max-w-md mx-auto">
                  We're preparing an in-depth video tutorial to help you understand our pricing and get the most value from MandaPost.
                </p>
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
              <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
              <div className="absolute bottom-10 right-10 w-40 h-40 bg-white/10 rounded-full blur-2xl"></div>
            </div>
          </div>

          {/* Video Benefits */}
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            <div className="text-center">
              <div className="w-12 h-12 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-timer-line text-white text-2xl"></i>
              </div>
              <h4 className="font-bold text-[#22577A] mb-2">Quick & Easy</h4>
              <p className="text-gray-600 text-sm">Learn the basics in under 5 minutes</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-lightbulb-line text-white text-2xl"></i>
              </div>
              <h4 className="font-bold text-[#22577A] mb-2">Pro Tips Included</h4>
              <p className="text-gray-600 text-sm">Get insider tips to maximize value</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-question-answer-line text-white text-2xl"></i>
              </div>
              <h4 className="font-bold text-[#22577A] mb-2">Common Questions</h4>
              <p className="text-gray-600 text-sm">Answers to frequently asked questions</p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <PricingComparison />

      {/* Competitor Pricing Comparison */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-[#e4f4ff]/30 rounded-full text-[#22577A] font-medium mb-6">
              <i className="ri-bar-chart-box-line mr-2"></i>
              Market Comparison
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#22577A] mb-6">
              How We Compare to Competitors
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              See how MandaPost stacks up against other leading social media management platforms
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-[#22577A] to-[#38a3a5] text-white">
                    <th className="px-6 py-4 text-left font-semibold">Platform</th>
                    <th className="px-6 py-4 text-center font-semibold">Starting Price</th>
                    <th className="px-6 py-4 text-center font-semibold">AI Features</th>
                    <th className="px-6 py-4 text-center font-semibold">Social Accounts</th>
                    <th className="px-6 py-4 text-center font-semibold">Team Members</th>
                    <th className="px-6 py-4 text-center font-semibold">Analytics</th>
                  </tr>
                </thead>
                <tbody>
                  {/* MandaPost - Highlighted */}
                  <tr className="bg-[#e4f4ff]/20 border-2 border-[#66b3ff]">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center">
                          <span className="text-white font-bold text-sm">MP</span>
                        </div>
                        <div>
                          <div className="font-bold text-[#22577A]">MandaPost</div>
                          <div className="text-xs text-[#66b3ff] font-semibold">YOU ARE HERE</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="font-bold text-[#22577A] text-lg">$29/mo</div>
                      <div className="text-xs text-gray-600">or $23/mo annual</div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="inline-flex items-center gap-1 px-3 py-1 bg-[#66b3ff] text-white rounded-full text-sm font-semibold">
                        <i className="ri-sparkling-line"></i>
                        Agentic AI
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center font-semibold text-[#22577A]">3-50</td>
                    <td className="px-6 py-4 text-center font-semibold text-[#22577A]">1-50</td>
                    <td className="px-6 py-4 text-center">
                      <i className="ri-check-line text-[#66b3ff] text-2xl"></i>
                    </td>
                  </tr>

                  {/* Hootsuite */}
                  <tr className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                          <span className="text-gray-600 font-bold text-sm">H</span>
                        </div>
                        <div className="font-semibold text-gray-700">Hootsuite</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="font-semibold text-gray-700">$99/mo</div>
                      <div className="text-xs text-gray-500">Professional</div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="inline-flex items-center gap-1 px-3 py-1 bg-gray-200 text-gray-600 rounded-full text-sm">
                        Limited
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center text-gray-600">10</td>
                    <td className="px-6 py-4 text-center text-gray-600">1</td>
                    <td className="px-6 py-4 text-center">
                      <i className="ri-check-line text-gray-400 text-2xl"></i>
                    </td>
                  </tr>

                  {/* Buffer */}
                  <tr className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                          <span className="text-gray-600 font-bold text-sm">B</span>
                        </div>
                        <div className="font-semibold text-gray-700">Buffer</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="font-semibold text-gray-700">$6/mo</div>
                      <div className="text-xs text-gray-500">per channel</div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="inline-flex items-center gap-1 px-3 py-1 bg-gray-200 text-gray-600 rounded-full text-sm">
                        Pablo AI
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center text-gray-600">1 per plan</td>
                    <td className="px-6 py-4 text-center text-gray-600">1</td>
                    <td className="px-6 py-4 text-center">
                      <i className="ri-check-line text-gray-400 text-2xl"></i>
                    </td>
                  </tr>

                  {/* Sprout Social */}
                  <tr className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                          <span className="text-gray-600 font-bold text-sm">SS</span>
                        </div>
                        <div className="font-semibold text-gray-700">Sprout Social</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="font-semibold text-gray-700">$249/mo</div>
                      <div className="text-xs text-gray-500">Standard</div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="inline-flex items-center gap-1 px-3 py-1 bg-gray-200 text-gray-600 rounded-full text-sm">
                        Basic AI
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center text-gray-600">5</td>
                    <td className="px-6 py-4 text-center text-gray-600">1</td>
                    <td className="px-6 py-4 text-center">
                      <i className="ri-check-line text-gray-400 text-2xl"></i>
                    </td>
                  </tr>

                  {/* Later */}
                  <tr className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                          <span className="text-gray-600 font-bold text-sm">L</span>
                        </div>
                        <div className="font-semibold text-gray-700">Later</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="font-semibold text-gray-700">$25/mo</div>
                      <div className="text-xs text-gray-500">Starter</div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="inline-flex items-center gap-1 px-3 py-1 bg-gray-200 text-gray-600 rounded-full text-sm">
                        None
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center text-gray-600">6</td>
                    <td className="px-6 py-4 text-center text-gray-600">1</td>
                    <td className="px-6 py-4 text-center">
                      <i className="ri-check-line text-gray-400 text-2xl"></i>
                    </td>
                  </tr>

                  {/* CoSchedule */}
                  <tr className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                          <span className="text-gray-600 font-bold text-sm">CS</span>
                        </div>
                        <div className="font-semibold text-gray-700">CoSchedule</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="font-semibold text-gray-700">$29/mo</div>
                      <div className="text-xs text-gray-500">Social Calendar</div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="inline-flex items-center gap-1 px-3 py-1 bg-gray-200 text-gray-600 rounded-full text-sm">
                        Limited
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center text-gray-600">10</td>
                    <td className="px-6 py-4 text-center text-gray-600">Unlimited</td>
                    <td className="px-6 py-4 text-center">
                      <i className="ri-check-line text-gray-400 text-2xl"></i>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Value Proposition Cards */}
          <div className="grid md:grid-cols-3 gap-8 mt-16">
            <div className="bg-gradient-to-br from-[#e4f4ff]/30 to-[#b3d9ff]/30 rounded-xl p-6 border-2 border-[#66b3ff]/30">
              <div className="w-12 h-12 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-money-dollar-circle-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-[#22577A] mb-2">Best Value</h3>
              <p className="text-gray-700">
                Get advanced AI features at a fraction of the cost. Our Professional plan at $79/mo offers more than competitors charging $250+/mo.
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#e4f4ff]/30 to-[#b3d9ff]/30 rounded-xl p-6 border-2 border-[#66b3ff]/30">
              <div className="w-12 h-12 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-robot-2-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-[#22577A] mb-2">AI-First Platform</h3>
              <p className="text-gray-700">
                Unlike competitors with basic AI, MandaPost is built with Agentic AI at its core, automating your entire content workflow.
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#e4f4ff]/30 to-[#b3d9ff]/30 rounded-xl p-6 border-2 border-[#66b3ff]/30">
              <div className="w-12 h-12 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-team-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-[#22577A] mb-2">Scale-Friendly</h3>
              <p className="text-gray-700">
                Start with 3 accounts and scale to 50+ without switching platforms. Flexible plans grow with your business.
              </p>
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-6 text-lg">
              Ready to save money while getting better AI features?
            </p>
            <a
              href="#"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#22577A] to-[#38a3a5] text-white rounded-lg font-semibold hover:shadow-xl transition-all cursor-pointer text-lg"
            >
              <i className="ri-rocket-line"></i>
              Start Your Free Trial
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <PricingFAQ />

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-[#22577A] to-[#38a3a5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Social Media Strategy?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Join thousands of marketers who trust MandaPost for their content creation
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#22577A] rounded-lg font-semibold hover:shadow-xl transition-all cursor-pointer"
            >
              <i className="ri-rocket-line"></i>
              Start Free Trial
            </a>
            <a
              href="#contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white text-white rounded-lg font-semibold hover:bg-white/10 transition-all cursor-pointer"
            >
              <i className="ri-customer-service-line"></i>
              Talk to Sales
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
